import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateClient } from '../contexts/clients';
import { useCreateFarm } from '../../farms/contexts/farms';
import { cleanName, orNull } from '../utils/normalize';
import { validateClient, validateFarm } from '../utils/validate';
import ClientFields from './fields';
import FarmRows from './farm-rows';

import '../styles/form.css';

const EMPTY_CLIENT = { name: '', document: '', email: '', phone: '', remarks: '' };

export default function CreateClientForm() {
    const navigate = useNavigate();
    const { mutateAsync: createClient } = useCreateClient();
    const { mutateAsync: createFarm } = useCreateFarm();

    const [client, setClient] = useState(EMPTY_CLIENT);
    const [farms, setFarms]   = useState([]);

    const [localErrors, setLocalErrors]   = useState({});   // client-side, by field
    const [serverFields, setServerFields] = useState({});   // backend Joi, by field
    const [farmErrors, setFarmErrors]     = useState({});   // by farm.key → { field: msg }

    const [submitting, setSubmitting] = useState(false);
    const [stage, setStage]           = useState('client'); // 'client' | 'farms'

    const farmSeq = useRef(0);

    const setClientField = (field, value) => {
        setClient(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    const addFarm = () => setFarms(prev => [
        ...prev,
        { key: `f${++farmSeq.current}`, name: '', size: '', address: '' },
    ]);
    const removeFarm = (key) => {
        setFarms(prev => prev.filter(f => f.key !== key));
        setFarmErrors(prev => ({ ...prev, [key]: undefined }));
    };
    const setFarmField = (key, field, value) => {
        setFarms(prev => prev.map(f => (f.key === key ? { ...f, [field]: value } : f)));
        setFarmErrors(prev => (prev[key] ? { ...prev, [key]: { ...prev[key], [field]: undefined } } : prev));
    };

    const farmPayload = (farm, ownerId) => ({
        name:     cleanName(farm.name),
        owner_id: ownerId,
        size:     farm.size !== '' ? Number(farm.size) : null,
        location: farm.address.trim() ? { address: farm.address.trim() } : null,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // ---- validate everything before any network call ----
        const clientErrs = validateClient(client);
        const farmErrs = {};
        farms.forEach((farm) => {
            const errs = validateFarm(farm);
            if (Object.keys(errs).length) farmErrs[farm.key] = errs;
        });

        if (Object.keys(clientErrs).length || Object.keys(farmErrs).length) {
            setLocalErrors(clientErrs);
            setFarmErrors(farmErrs);
            toast.error('Revisá los campos marcados');
            return;
        }
        setLocalErrors({});
        setFarmErrors({});
        setServerFields({});

        // ---- 1) create the client ----
        setSubmitting(true);
        setStage('client');
        let created;
        try {
            created = await createClient({
                name:     cleanName(client.name),
                document: orNull(client.document),
                email:    orNull(client.email),
                phone:    orNull(client.phone),
                remarks:  orNull(client.remarks),
            });
        } catch (err) {
            setSubmitting(false);
            if (err?.status === 409) toast.error('Ya existe un cliente con ese documento');
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo crear el cliente');
            return;
        }

        // ---- 2) queue the farm creations (sequential, owner = new client) ----
        const failed = [];
        if (farms.length) {
            setStage('farms');
            for (const farm of farms) {
                try {
                    await createFarm(farmPayload(farm, created.id));
                } catch {
                    failed.push(cleanName(farm.name) || 'finca');
                }
            }
        }

        setSubmitting(false);

        if (failed.length) {
            toast.error(`Cliente creado. No se pudieron crear: ${failed.join(', ')}`);
        } else if (farms.length) {
            toast.success(`Cliente y ${farms.length} finca(s) creados`);
        } else {
            toast.success('Cliente creado');
        }
        navigate('/admin/clients');
    };

    const submitLabel = submitting
        ? (stage === 'farms' ? 'Creando fincas…' : 'Creando cliente…')
        : 'Crear cliente';

    return (
        <form className="client-form" onSubmit={handleSubmit}>
            <header className="client-form__header">
                <Link to="/admin/clients" className="client-form__back">Clientes</Link>
                <h1 className="client-form__title">Nuevo cliente</h1>
            </header>

            <section className="client-form__section">
                <h2 className="client-form__section-title">Datos del cliente</h2>
                <ClientFields
                    form={client}
                    setField={setClientField}
                    fieldError={fieldError}
                    disabled={submitting}
                />
            </section>

            <FarmRows
                farms={farms}
                onAdd={addFarm}
                onRemove={removeFarm}
                onField={setFarmField}
                errors={farmErrors}
                disabled={submitting}
            />

            <footer className="client-form__footer">
                <Link to="/admin/clients" className="client-form__cancel">Cancelar</Link>
                <button type="submit" className="client-form__submit" disabled={submitting}>
                    {submitLabel}
                </button>
            </footer>
        </form>
    );
}
